import { attachmentsApi, type AttachmentEntityType } from './api';

// Client-side mirror of the server's attachment constraints (see
// apps/backend/src/routes/attachments.ts). Kept in sync so oversized or
// wrong-type files are rejected before staging, instead of failing after Save.

export const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024; // 25 MB

export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  '.jpg', '.jpeg', '.png', '.webp', '.pdf', '.xlsx', '.xls', '.docx',
] as const;

const ALLOWED_MIME_TYPES = new Set([
  'image/jpeg',
  'image/png',
  'image/webp',
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

/**
 * Validate a file against the same rules the upload endpoint enforces.
 * Returns a human-readable error string, or null if the file is acceptable.
 */
export function attachmentValidationError(file: File): string | null {
  const dot = file.name.lastIndexOf('.');
  const ext = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
  if (!(ALLOWED_ATTACHMENT_EXTENSIONS as readonly string[]).includes(ext)) {
    return `${file.name}: unsupported file type. Allowed: images, PDF, Word, Excel.`;
  }
  // Browsers sometimes report an empty type; only reject a non-empty mismatch.
  if (file.type && !ALLOWED_MIME_TYPES.has(file.type)) {
    return `${file.name}: unsupported file type.`;
  }
  if (file.size > MAX_ATTACHMENT_BYTES) {
    return `${file.name}: too large (max 25 MB).`;
  }
  return null;
}

/**
 * Upload a set of already-staged files to an existing entity, sequentially
 * (one per request, matching the single-file API). Failures are collected
 * rather than thrown, so a mid-list failure doesn't discard the successful
 * uploads. Returns the filenames that failed.
 */
export async function uploadStagedAttachments(
  entityType: AttachmentEntityType,
  entityId: string,
  files: File[],
): Promise<{ failed: string[] }> {
  const failed: string[] = [];
  for (const file of files) {
    try {
      await attachmentsApi.upload(entityType, entityId, file);
    } catch {
      failed.push(file.name);
    }
  }
  return { failed };
}
